import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import type {
  ChannelRecord,
  RoutingBreakerRecord,
  RoutingConfigAssignmentRecord,
  RoutingConfigSummaryRecord,
  RoutingPolicyMode,
  RoutingSkuTargetRecord,
  RoutingTimeWindowRecord,
  UpstreamProtocol,
} from '@nadoutong/contracts';
import { useChannelModelsList } from '../api/useChannelModels.ts';
import { useResetRoutingBreaker, useRoutingBreakers } from '../api/useRoutingBreakers.ts';
import { useTargetProbes } from '../api/useTargetProbes.ts';
import {
  TENANT_ROUTING_KEY,
  useDeleteRoutingConfigSkuTarget,
  useRoutingConfigs,
  useRoutingConfigSkuTargetsMap,
  useRoutingConfigSkuPoliciesMap,
  useRoutingConfigTimeWindows,
  useSetRoutingConfigCoverageMode,
  useSetRoutingConfigSkuPolicy,
  useSetRoutingConfigSkuTarget,
} from '../api/useTenantRouting.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Chip, ConfirmDialog, Drawer, FormField, Select } from '../ui/index.ts';
import { RoutingBreakerTripDialog } from './RoutingBreakerTripDialog.tsx';
import type { BreakerTripTarget } from './RoutingBreakerTripDialog.tsx';
import { routingTargetWriteBody } from './guaranteed/routingTargetWrite.ts';
import { skuTargetCapBody, TenantRoutingTargetDialog } from './TenantRoutingTargetDialog.tsx';
import type { SkuTargetDialogSubmission } from './TenantRoutingTargetDialog.tsx';
import { TenantRoutingAssignedCustomers } from './tenant-routing/TenantRoutingAssignedCustomers.tsx';
import { TenantRoutingCoverageFillDialog } from './tenant-routing/TenantRoutingCoverageFillDialog.tsx';
import { TenantRoutingTimeWindowDialog } from './tenant-routing/TenantRoutingTimeWindowDialog.tsx';
import { TenantRoutingSkuTree } from './tenant-routing/TenantRoutingSkuTree.tsx';
import { TenantRoutingSkuPoolPanel } from './tenant-routing/TenantRoutingSkuPoolPanel.tsx';
import { namespaceMissingSkus } from './tenant-routing/skuCoverage.ts';
import {
  buildNamespaceSkuGroups,
  countBlockingBreakers,
  findRowMatchedByUpsert,
  formatBreakerIdentity,
  formatSkuTargetIdentity,
  routeConfigStatusLabelKey,
  routeConfigTypeLabelKey,
  isDeprecated,
} from './tenantRoutingShared.ts';
import type { NamespaceSkuGroup, SkuOption } from './tenantRoutingShared.ts';

/**
 * Config detail sheet (epic #886 Design B, re-keyed to SKUs by epic #898 P5,
 * re-laid-out by #1493): a wide right-hand drawer split into a namespace → SKU
 * tree on the left and the ONE selected SKU's editable target pool + execution
 * policy on the right, above the assigned-customer list. It was a single
 * column of stacked per-SKU sections until #1493, which meant one table header
 * and one policy selector per catalog SKU.
 *
 * The gateway only exposes per-SKU pool GETs, so the sheet still fans out one
 * query per catalog SKU and merges the results — the fan-out is per open, not
 * per selection, and moving through the tree issues no request.
 */

interface TargetEditorState {
  readonly sku: SkuOption;
  readonly group: NamespaceSkuGroup;
  readonly target: RoutingSkuTargetRecord | null;
}

interface TargetDeleteDraft {
  readonly sku: SkuOption;
  readonly target: RoutingSkuTargetRecord;
}

function lastEnabledProtocolsForTarget(
  target: RoutingSkuTargetRecord,
  sameConfigSkuTargets: readonly RoutingSkuTargetRecord[],
  liveProtocolsByTargetId: ReadonlyMap<string, readonly UpstreamProtocol[]>,
): readonly UpstreamProtocol[] {
  if (!target.enabled) return [];
  const targetProtocols = liveProtocolsByTargetId.get(target.id) ?? [];
  return targetProtocols.filter((protocol) => !sameConfigSkuTargets.some((candidate) =>
    candidate.id !== target.id &&
    candidate.sku_id === target.sku_id &&
    candidate.enabled &&
    (liveProtocolsByTargetId.get(candidate.id) ?? []).includes(protocol)
  ));
}

interface Props {
  readonly configId: string | null;
  readonly onClose: () => void;
  readonly channels: readonly ChannelRecord[];
  readonly assignments: readonly RoutingConfigAssignmentRecord[];
  readonly onRename: (config: RoutingConfigSummaryRecord) => void;
  readonly onClone: (config: RoutingConfigSummaryRecord) => void;
  readonly onAssign: (configId: string) => void;
  readonly onSetGlobal: (configId: string) => void;
  /** Opens the page-level breakers drawer (mounted by TenantRoutingPage). */
  readonly onOpenBreakers: () => void;
  readonly renaming: boolean;
  readonly assigning: boolean;
  readonly settingGlobal: boolean;
}

export function TenantRoutingConfigSheet({
  configId,
  onClose,
  channels,
  assignments,
  onRename,
  onClone,
  onAssign,
  onSetGlobal,
  onOpenBreakers,
  renaming,
  assigning,
  settingGlobal,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const routingConfigs = useRoutingConfigs();
  const channelModels = useChannelModelsList();
  const setTarget = useSetRoutingConfigSkuTarget();
  const deleteTarget = useDeleteRoutingConfigSkuTarget();
  const setPolicy = useSetRoutingConfigSkuPolicy();
  const setCoverageMode = useSetRoutingConfigCoverageMode();
  const routingBreakers = useRoutingBreakers();
  const resetBreaker = useResetRoutingBreaker();
  const [targetEditor, setTargetEditor] = useState<TargetEditorState | null>(null);
  const [targetDeleteDraft, setTargetDeleteDraft] = useState<TargetDeleteDraft | null>(null);
  const [breakerResetDraft, setBreakerResetDraft] = useState<RoutingBreakerRecord | null>(null);
  const [breakerTripTarget, setBreakerTripTarget] = useState<BreakerTripTarget | null>(null);
  const [collapsedNamespaces, setCollapsedNamespaces] = useState<ReadonlySet<string>>(() => new Set());
  const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null);
  // `?sku=<id>` next to `?config=` (the 重点保障中心 leg deep link) preselects
  // the SKU; an explicit click still wins.
  const [searchParams] = useSearchParams();
  const deepLinkSkuId = searchParams.get('sku');
  /** Namespace whose coverage-fill dialog is open (`null` = closed). */
  const [coverageGroup, setCoverageGroup] = useState<NamespaceSkuGroup | null>(null);
  /**
   * Time-window editor state (0044). `{ window: null }` is "create", which is
   * distinguishable from "closed" (`null`) — a plain `RoutingTimeWindowRecord
   * | null` could not express both.
   */
  const [timeWindowEditor, setTimeWindowEditor] =
    useState<{ readonly window: RoutingTimeWindowRecord | null } | null>(null);

  useEffect(() => {
    if (configId === null) {
      setTargetEditor(null);
      setTargetDeleteDraft(null);
      setBreakerResetDraft(null);
      setBreakerTripTarget(null);
      setCollapsedNamespaces(new Set());
      setSelectedSkuId(null);
      setCoverageGroup(null);
      setTimeWindowEditor(null);
    }
  }, [configId]);

  const breakerRecords = routingBreakers.data?.breakers ?? [];
  const targetProbes = useTargetProbes();
  const probeRecords = targetProbes.data?.states ?? [];
  // Same open + forced counting rule as the page health strip, so the
  // summary line always matches the panel the button opens.
  const blockingBreakerCount = countBlockingBreakers(breakerRecords);

  const config = useMemo(
    () => configId === null
      ? null
      : (routingConfigs.data?.configs ?? []).find((candidate) => candidate.id === configId) ?? null,
    [configId, routingConfigs.data],
  );
  const skuGroups = useMemo(
    () => buildNamespaceSkuGroups(channelModels.data ?? [], channels),
    [channelModels.data, channels],
  );
  const skuIds = useMemo(
    () => skuGroups.flatMap((group) => group.skus.map((sku) => sku.skuId)),
    [skuGroups],
  );
  const skuTargets = useRoutingConfigSkuTargetsMap(configId, skuIds);
  const skuPolicies = useRoutingConfigSkuPoliciesMap(configId, skuIds);
  // One read for the whole config: a window group's overrides may span SKUs.
  const timeWindows = useRoutingConfigTimeWindows(configId);
  /**
   * The SKU whose pool the right column renders (#1493). Derived rather than
   * an effect: the sheet mounts before the fan-out resolves, so "first SKU
   * that actually has targets" is only knowable later, and an effect would
   * write that answer back into state on the render it became true — an extra
   * pass, and a race with an operator who clicked in the meantime. Here the
   * explicit pick simply wins as soon as it exists, and it is validated
   * against the live catalog so a vanished SKU falls back instead of leaving
   * the pane blank.
   *
   * Everything below reads maps the sheet has already loaded, so changing the
   * selection issues no request.
   */
  const selection = useMemo(() => {
    const allSkus = skuGroups.flatMap((group) => group.skus.map((sku) => ({ sku, group })));
    const wanted = selectedSkuId ?? deepLinkSkuId;
    const explicit = wanted === null
      ? undefined
      : allSkus.find((entry) => entry.sku.skuId === wanted);
    if (explicit !== undefined) return explicit;
    // `detailPending` below does not wait on the pool fan-out, so without this
    // the sheet paints with an empty `targetsBySku`, defaults to the
    // catalog-first SKU, and then jumps to the first SKU that turned out to
    // have targets. The right pane is keyed by SKU id, so that jump remounts
    // it and silently discards a policy draft the operator may already have
    // changed. Guessing is only free before anything is on screen; hold the
    // pane instead.
    if (skuTargets.isPending) return null;
    return allSkus.find((entry) => (skuTargets.targetsBySku.get(entry.sku.skuId)?.length ?? 0) > 0)
      ?? allSkus[0]
      ?? null;
  }, [deepLinkSkuId, selectedSkuId, skuGroups, skuTargets.isPending, skuTargets.targetsBySku]);
  const availableIdentities = useMemo(() => {
    const byKey = new Map<string, RoutingSkuTargetRecord>();
    for (const targets of skuTargets.targetsBySku.values()) {
      for (const target of targets) {
        // Legacy unpinned rows cannot safely seed another SKU: there is no
        // physical supply evidence to carry into the required write.
        if (target.capability_id === null) continue;
        const key = [target.capability_id, target.upstream_id, target.account_id, target.key_bucket_id, target.upstream_channel_id].join('\u0000');
        if (!byKey.has(key)) byKey.set(key, target);
      }
    }
    return byKey;
  }, [skuTargets.targetsBySku]);
  // A just-created / just-cloned config may not be in the cached list yet;
  // treat "unknown id while the list refetches" as loading, not an error.
  const detailPending = routingConfigs.isPending ||
    channelModels.isPending ||
    (config === null && routingConfigs.isFetching);
  const targetDeleteLastEnabledProtocols = useMemo(() => {
    if (targetDeleteDraft === null) return [];
    return lastEnabledProtocolsForTarget(
      targetDeleteDraft.target,
      skuTargets.targetsBySku.get(targetDeleteDraft.sku.skuId) ?? [],
      skuTargets.liveProtocolsByTargetId,
    );
  }, [skuTargets.liveProtocolsByTargetId, skuTargets.targetsBySku, targetDeleteDraft]);

  function toggleNamespace(channelId: string): void {
    setCollapsedNamespaces((previous) => {
      const next = new Set(previous);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        next.add(channelId);
      }
      return next;
    });
  }

  /**
   * One exact capability-pinned PUT for the scoped SKU. Sibling models must
   * be configured separately because their physical supply cannot be guessed
   * from this SKU's selected capability.
   *
   * Both paths need a guard before they write (#1457 — the config-scoped twin
   * of #1431). Whether a PUT inserts or updates is the server's decision:
   * `upsertSkuTarget` looks the row up under `config_id = $1` on the physical
   * four-tuple with `(capability_id = $n OR capability_id IS NULL)`, then
   * writes `before?.id ?? id`. `TenantRoutingTargetDialog` offers every
   * capability under the chosen four-tuple that this SKU's supply-candidate
   * read calls bindable — it has never heard of this config's pool — so a
   * submission can land on EITHER arm, and on a row that is not the one being
   * edited:
   *
   *   - ADD: an existing lane is relocated and its weight / retry order /
   *     enabled flag are overwritten from a draft the operator believed was
   *     describing a NEW row. Row count unchanged, success toast.
   *   - EDIT re-pointed onto a lane another row already owns: the server
   *     rewrites THAT row, and the cleanup below then deletes the edited one.
   *     Row count MINUS ONE, success toast.
   *
   * So the single check is "would this PUT land on a row other than the one
   * this dialog is editing?" — `null` for an add, so any match refuses.
   */
  async function handleTargetSubmit(submission: SkuTargetDialogSubmission): Promise<void> {
    if (configId === null || targetEditor === null) return;
    const skuId = targetEditor.sku.skuId;
    const previous = targetEditor.target;
    // The guard is only as good as the pool it searches, and success is the
    // freshness test, not emptiness: a first read that failed leaves this SKU
    // absent from the map, a failed background refetch leaves React Query's
    // last good payload in place. Either way this write's insert-or-update
    // fate cannot be vouched for, so it is refused rather than attempted. Per
    // SKU — a sibling SKU's failure is not this one's.
    if (!skuTargets.loadedSkuIds.has(skuId)) {
      toast.error(t('tenant_routing.pool.write_blocked_targets_unknown'));
      return;
    }
    const matched = findRowMatchedByUpsert(skuTargets.targetsBySku.get(skuId) ?? [], submission);
    if (matched !== null && matched.id !== previous?.id) {
      // Two arms, two remedies — and in this scope neither remedy is a new
      // flow. A pinned collision is already editable in place; an unpinned one
      // is repaired by editing THAT row and choosing a capability there, which
      // the fixed cleanup below now performs as a true in-place update. Both
      // name the row by id. The dialog stays open so the operator can pick
      // another lane instead of losing the draft.
      toast.error(t(
        matched.capability_id === null
          ? 'tenant_routing.pool.write_hits_unpinned_row'
          : 'tenant_routing.pool.write_hits_bound_row',
        { channel: matched.upstream_channel_slug, id: matched.id },
      ));
      return;
    }
    const affectedSkus = [targetEditor.sku];
    const failures: { readonly sku: SkuOption; readonly message: string }[] = [];
    let writtenId: string | null = null;
    for (const sku of affectedSkus) {
      try {
        const written = await setTarget.mutateAsync({
          configId,
          skuId: sku.skuId,
          body: {
            capability_id: submission.capability_id,
            sku_name: sku.skuName,
            vendor: sku.vendor,
            model: sku.model,
            upstream_id: submission.upstream_id,
            account_id: submission.account_id,
            key_bucket_id: submission.key_bucket_id,
            upstream_channel_id: submission.upstream_channel_id,
            weight: submission.weight,
            retry_order: submission.retry_order,
            enabled: submission.enabled,
            ...(submission.smart_role !== undefined ? { smart_role: submission.smart_role } : {}),
            ...(submission.smart_initial_score !== undefined
              ? { smart_initial_score: submission.smart_initial_score }
              : {}),
            ...(submission.disabled_note !== undefined ? { disabled_note: submission.disabled_note } : {}),
            ...skuTargetCapBody(submission),
          },
        });
        writtenId = written.id;
      } catch (error) {
        failures.push({ sku, message: coerceErrorMessage(error) });
      }
    }
    if (failures.length > 0) {
      for (const failure of failures) {
        toast.error(t('tenant_routing.sku_pools.save_failed', {
          sku: failure.sku.skuName,
          message: failure.message,
        }));
      }
      return;
    }
    // PUT upserts one exact physical pin. Remove only the edited prior row
    // when either its physical pin/protocol or four-part supply identity
    // changed; the id-scoped DELETE deliberately preserves valid sibling
    // targets for other capabilities/protocols.
    //
    // `writtenId !== previous.id` is the load-bearing half, and it reads the
    // SERVER's answer rather than predicting it. An unpinned row edited into a
    // pinned one changes "identity" by every field test below, yet the server
    // matched that very row through its `capability_id IS NULL` arm and
    // updated it in place — deleting `previous.id` afterwards would remove the
    // row that was just written and report success over a pool one row
    // shorter. The PUT response says which row it wrote; nothing else here has
    // to guess.
    if (
      previous !== null &&
      writtenId !== previous.id &&
      (previous.capability_id !== submission.capability_id ||
        previous.capability_protocol !== submission.capability_protocol ||
        previous.upstream_id !== submission.upstream_id ||
        previous.account_id !== submission.account_id ||
        previous.key_bucket_id !== submission.key_bucket_id ||
        previous.upstream_channel_id !== submission.upstream_channel_id)
    ) {
      try {
        await deleteTarget.mutateAsync({
          configId,
          skuId: targetEditor.sku.skuId,
          targetId: previous.id,
        });
      } catch {
        // The PUT landed but the old row is still live — the pool now
        // carries BOTH identities and live traffic splits across them.
        // Name the exact stale row, refetch so the duplicate is visible in
        // the matrix, and keep the dialog open: re-saving retries the
        // cleanup (the PUT upsert is idempotent).
        toast.error(t('tenant_routing.sku_pools.stale_row_warning', { id: previous.id }));
        void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
        return;
      }
    }
    // Highest-stakes mutation on this surface: link straight to the
    // upstream that was wired in (issue #856 pattern).
    toast.success(t('tenant_routing.pool.toast_saved'), {
      action: {
        label: t('tenant_routing.toast.action.view_upstream'),
        to: `/supply/upstreams/${encodeURIComponent(submission.upstream_id)}`,
      },
    });
    setTargetEditor(null);
  }

  function handleTargetDelete(sku: SkuOption, target: RoutingSkuTargetRecord): void {
    setTargetDeleteDraft({ sku, target });
  }

  function handleTargetDeleteConfirm(): void {
    if (configId === null || targetDeleteDraft === null) return;
    deleteTarget.mutate(
      {
        configId,
        skuId: targetDeleteDraft.sku.skuId,
        targetId: targetDeleteDraft.target.id,
      },
      {
        onSuccess: () => {
          setTargetDeleteDraft(null);
          toast.success(t('tenant_routing.pool.toast_deleted'));
        },
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  function handleBreakerTrip(target: RoutingSkuTargetRecord): void {
    // The identity four-tuple lives on the target record itself, so a
    // healthy target can be forced out without an existing breaker entry.
    const disabled = routingTargetWriteBody(target, false);
    const permanentDisable = disabled === null || configId === null
      ? undefined
      : {
        layer: 'config' as const,
        configId,
        skuId: target.sku_id,
        body: disabled,
      };
    setBreakerTripTarget({
      identity: {
        upstream_id: target.upstream_id,
        account_id: target.account_id,
        key_bucket_id: target.key_bucket_id,
        upstream_channel_id: target.upstream_channel_id,
      },
      label: `${formatSkuTargetIdentity(target)} · ${target.upstream_channel_slug}`,
      ...(permanentDisable === undefined ? {} : { permanentDisable }),
    });
  }

  function handleBreakerResetConfirm(): void {
    if (breakerResetDraft === null) return;
    resetBreaker.mutate(
      {
        upstream_id: breakerResetDraft.upstream_id,
        account_id: breakerResetDraft.account_id,
        key_bucket_id: breakerResetDraft.key_bucket_id,
        upstream_channel_id: breakerResetDraft.upstream_channel_id,
      },
      {
        onSuccess: () => {
          setBreakerResetDraft(null);
          toast.success(t('tenant_routing.breaker.toast.reset'));
        },
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  function handlePolicySave(sku: SkuOption, policyMode: RoutingPolicyMode): void {
    if (configId === null) return;
    setPolicy.mutate(
      {
        configId,
        skuId: sku.skuId,
        body: {
          sku_name: sku.skuName,
          vendor: sku.vendor,
          model: sku.model,
          policy_mode: policyMode,
        },
      },
      {
        onSuccess: () => toast.success(t('tenant_routing.toast.policy_saved')),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return (
    <>
      <Drawer
        open={configId !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        width="2xl"
        title={config?.name ?? t('tenant_routing.loading')}
        description={configId === null ? undefined : <span className="font-mono">{configId}</span>}
        closeLabel={t('common.close')}
        testId="tenant-routing-config-sheet"
      >
        {detailPending && configId !== null ? (
          <p className="text-body text-paper-low">{t('tenant_routing.loading')}</p>
        ) : routingConfigs.isError || (configId !== null && config === null) ? (
          <p className="text-body text-bad" data-testid="tenant-routing-config-sheet-error">
            {t('tenant_routing.sheet.error')}
          </p>
        ) : config === null ? null : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Chip tone="neutral" lamp={false}>{t(routeConfigTypeLabelKey(config.config_type))}</Chip>
                <Chip tone={config.status === 'active' ? 'ok' : 'neutral'}>
                  {t(routeConfigStatusLabelKey(config.status))}
                </Chip>
                {config.is_global_default ? (
                  <Chip tone="signal" lamp={false}>{t('tenant_routing.configs.global_default_badge')}</Chip>
                ) : null}
                {/* #1919. The deprecated group in the library is one way in;
                    a deep link with `?config=<id>` is another, and it lands
                    here regardless of grouping. Without this badge the sheet
                    silently presents a config the assign button is about to
                    refuse. */}
                {isDeprecated(config) ? (
                  <Chip tone="warn" lamp={false}>
                    {t('tenant_routing.deprecation.badge')}
                  </Chip>
                ) : null}
                <span className="font-mono text-micro text-paper-low">
                  v{config.version} · {formatLocalTimestamp(config.updated_at)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={renaming || config.status === 'archived'}
                  onClick={() => onRename(config)}
                  data-testid={`tenant-routing-config-rename-${config.id}`}
                >
                  {t('tenant_routing.configs.rename')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => onClone(config)}
                  data-testid={`tenant-routing-config-clone-${config.id}`}
                >
                  {t('tenant_routing.configs.clone')}
                </Button>
                <Button
                  size="sm"
                  disabled={
                    settingGlobal
                    || config.is_global_default
                    || config.status !== 'active'
                    // #1919. Promoting a config marked "no new bindings" to
                    // serve every org on the global default is the largest new
                    // binding there is. DR plans and the emergency override
                    // deliberately stay open on deprecated configs — an
                    // incident may need exactly the config nobody sells any
                    // more — but that is a break-glass act, and this is not.
                    || isDeprecated(config)
                  }
                  onClick={() => onSetGlobal(config.id)}
                  data-testid={`tenant-routing-config-make-global-${config.id}`}
                >
                  {t('tenant_routing.configs.make_global')}
                </Button>
                <Button
                  size="sm"
                  disabled={assigning || config.status !== 'active' || isDeprecated(config)}
                  onClick={() => onAssign(config.id)}
                  data-testid={`tenant-routing-config-assign-${config.id}`}
                >
                  {t('tenant_routing.configs.assign')}
                </Button>
              </div>
            </div>

            {config.coverage_mode === 'exclusive' ? (
              <div role="alert" className="rounded-md border border-warn-line bg-warn-dim/50 px-3 py-2.5 text-dense leading-5 text-warn" data-testid="tenant-routing-exclusive-coverage-banner">
                <div className="flex items-start justify-between gap-3">
                  <span className="flex items-start gap-2"><AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />{t('tenant_routing.coverage.exclusive_warning')}</span>
                  <FormField
                    label={t('tenant_routing.coverage.mode_label')}
                    fieldId="tenant-routing-coverage-mode"
                    className="w-44 shrink-0"
                  >
                    <Select
                      id="tenant-routing-coverage-mode"
                      ariaLabel={t('tenant_routing.coverage.mode_label')}
                      value={config.coverage_mode}
                      onValueChange={(value) => setCoverageMode.mutate({ configId: config.id, body: { coverage_mode: value === 'inherit' ? 'inherit' : 'exclusive' } })}
                      options={[
                        { value: 'exclusive', label: t('tenant_routing.coverage.mode_exclusive') },
                        { value: 'inherit', label: t('tenant_routing.coverage.mode_inherit') },
                      ]}
                      disabled={setCoverageMode.isPending || config.status === 'archived'}
                      testId="tenant-routing-coverage-mode"
                    />
                  </FormField>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 rounded-md bg-well px-3 py-2 text-dense text-paper-low">
                <span>{t('tenant_routing.coverage.inherit_hint')}</span>
                <FormField
                  label={t('tenant_routing.coverage.mode_label')}
                  fieldId="tenant-routing-coverage-mode"
                  className="w-44 shrink-0"
                >
                  <Select
                    id="tenant-routing-coverage-mode"
                    ariaLabel={t('tenant_routing.coverage.mode_label')}
                    value="inherit"
                    onValueChange={(value) => setCoverageMode.mutate({ configId: config.id, body: { coverage_mode: value === 'exclusive' ? 'exclusive' : 'inherit' } })}
                    options={[
                      { value: 'exclusive', label: t('tenant_routing.coverage.mode_exclusive') },
                      { value: 'inherit', label: t('tenant_routing.coverage.mode_inherit') },
                    ]}
                    disabled={setCoverageMode.isPending || config.status === 'archived'}
                    testId="tenant-routing-coverage-mode"
                  />
                </FormField>
              </div>
            )}

            {config.description !== null && config.description !== undefined && config.description.length > 0 ? (
              <p className="m-0 rounded-md bg-well px-3 py-2 text-dense leading-5 text-paper-low">
                {config.description}
              </p>
            ) : null}

            {(config.assigned_org_count ?? 0) >= 1 ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-warn-line bg-warn-dim/50 px-3 py-2.5 text-dense leading-5 text-warn"
                data-testid="tenant-routing-shared-impact-warning"
              >
                <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {t('tenant_routing.sheet.shared_warning', {
                    count: String(config.assigned_org_count),
                  })}
                </span>
              </div>
            ) : null}

            <div
              className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-well px-3 py-2"
              data-testid="tenant-routing-sheet-breaker-summary"
            >
              <span className="flex items-center gap-2 text-dense">
                <span
                  aria-hidden="true"
                  className={[
                    'size-1.5 rounded-full',
                    blockingBreakerCount > 0 ? 'bg-bad' : 'bg-paper-faint',
                  ].join(' ')}
                />
                <span
                  className={blockingBreakerCount > 0 ? 'font-medium text-bad' : 'text-paper-low'}
                  data-testid="tenant-routing-sheet-breaker-summary-count"
                >
                  {t(
                    blockingBreakerCount === 1
                      ? 'tenant_routing.breaker.sheet_summary_one'
                      : 'tenant_routing.breaker.sheet_summary_many',
                    { count: String(blockingBreakerCount) },
                  )}
                </span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={onOpenBreakers}
                data-testid="tenant-routing-sheet-open-breakers"
              >
                {t('tenant_routing.breaker.open_panel')}
              </Button>
            </div>

            <section className="flex flex-col gap-3" data-testid="tenant-routing-config-pools">
              <div>
                <h3 className="m-0 text-section text-paper">{t('tenant_routing.sku_pools.title')}</h3>
                <p className="m-0 mt-1 text-dense leading-5 text-paper-low">{t('tenant_routing.sku_pools.hint')}</p>
              </div>
              <div className="flex gap-4">
                <TenantRoutingSkuTree
                  groups={skuGroups}
                  collapsedNamespaces={collapsedNamespaces}
                  onToggleNamespace={toggleNamespace}
                  targetsBySku={skuTargets.targetsBySku}
                  policiesBySku={skuPolicies.policiesBySku}
                  selectedSkuId={selection?.sku.skuId ?? null}
                  onSelectSku={(sku) => setSelectedSkuId(sku.skuId)}
                  onPreviewFill={setCoverageGroup}
                />
                {selection === null ? (
                  // Two distinct states behind one `null`: the fan-out has not
                  // answered yet (nothing to select), versus the catalog has no
                  // SKUs at all (nothing to ever select). They ask different
                  // things of the operator, so they never share a message.
                  <p
                    className="m-0 min-w-0 flex-1 rounded-lg border border-line-soft bg-well px-4 py-3 text-dense text-paper-low"
                    data-testid={skuTargets.isPending
                      ? 'tenant-routing-sku-pool-pending'
                      : 'tenant-routing-sku-pool-empty'}
                  >
                    {t(skuTargets.isPending
                      ? 'tenant_routing.sku_pools.loading_pools'
                      : 'tenant_routing.sku_pools.no_selection')}
                  </p>
                ) : (
                  <TenantRoutingSkuPoolPanel
                    key={selection.sku.skuId}
                    sku={selection.sku}
                    targets={skuTargets.targetsBySku.get(selection.sku.skuId)}
                    targetsLoaded={skuTargets.loadedSkuIds.has(selection.sku.skuId)}
                    savedPolicyMode={
                      skuPolicies.policiesBySku.get(selection.sku.skuId)?.policy_mode ?? 'full_chain'
                    }
                    policyLoaded={
                      // Per-SKU, matching `targetsLoaded` above. `isPending` is
                      // true while ANY policy query in the namespace is in
                      // flight, so one slow unrelated SKU used to hold this
                      // SKU's save button disabled — silently, since unlike the
                      // add button it carries no explanatory title.
                      // `policiesBySku` is only written once a query resolved,
                      // so `has()` is exactly "this SKU's own read landed".
                      skuPolicies.policiesBySku.has(selection.sku.skuId) || !skuPolicies.isPending
                    }
                    breakers={breakerRecords}
                    probes={probeRecords}
                    probeLayer="config"
                    deleting={deleteTarget.isPending}
                    savingPolicy={setPolicy.isPending}
                    resettingBreaker={resetBreaker.isPending}
                    onAdd={() => setTargetEditor({ sku: selection.sku, group: selection.group, target: null })}
                    onEdit={(target) => setTargetEditor({ sku: selection.sku, group: selection.group, target })}
                    onDelete={(target) => handleTargetDelete(selection.sku, target)}
                    onPolicySave={(mode) => handlePolicySave(selection.sku, mode)}
                    onBreakerReset={setBreakerResetDraft}
                    onBreakerTrip={handleBreakerTrip}
                    savingSmartFields={setTarget.isPending}
                    onSaveSmartFields={(target, patch) => {
                      if (target.capability_id === null) return;
                      void setTarget.mutateAsync({
                        configId: config.id,
                        skuId: selection.sku.skuId,
                        body: {
                          capability_id: target.capability_id,
                          sku_name: selection.sku.skuName,
                          vendor: selection.sku.vendor,
                          model: selection.sku.model,
                          upstream_id: target.upstream_id,
                          account_id: target.account_id,
                          key_bucket_id: target.key_bucket_id,
                          upstream_channel_id: target.upstream_channel_id,
                          ...(patch.smart_role !== undefined ? { smart_role: patch.smart_role } : {}),
                          ...(patch.smart_initial_score !== undefined
                            ? { smart_initial_score: patch.smart_initial_score }
                            : {}),
                        },
                      }).then(
                        () => toast.success(t('tenant_routing.pool.toast_saved')),
                        (error: unknown) => toast.error(coerceErrorMessage(error)),
                      );
                    }}
                    timeWindows={timeWindows.data?.windows ?? []}
                    // Fail-closed like the add-target button: a window write
                    // is refused server-side when it overlaps an existing one,
                    // and this sheet cannot say what it would overlap with
                    // until the read lands.
                    timeWindowsLoaded={timeWindows.isSuccess}
                    onAddTimeWindow={() => setTimeWindowEditor({ window: null })}
                    onEditTimeWindow={(window) => setTimeWindowEditor({ window })}
                  />
                )}
              </div>
            </section>

            <TenantRoutingAssignedCustomers
              configId={config.id}
              assignedOrgCount={config.assigned_org_count ?? 0}
              assignments={assignments}
            />
          </div>
        )}
      </Drawer>

      {targetEditor !== null ? (
        <TenantRoutingTargetDialog
          open
          sku={targetEditor.sku}
          namespaceChannel={targetEditor.group.channel}
          namespaceSkuCount={targetEditor.group.skus.length}
          target={targetEditor.target}
          saving={setTarget.isPending || deleteTarget.isPending}
          onClose={() => setTargetEditor(null)}
          onSubmit={(submission) => {
            void handleTargetSubmit(submission);
          }}
        />
      ) : null}

      {coverageGroup === null || config === null ? null : (
        <TenantRoutingCoverageFillDialog
          configId={config.id}
          configName={config.name}
          assignedOrgCount={config.assigned_org_count ?? 0}
          channelSlug={coverageGroup.channel.slug}
          missingSkus={namespaceMissingSkus(
            coverageGroup,
            skuTargets.targetsBySku,
            skuPolicies.policiesBySku,
          )}
          availableIdentities={availableIdentities}
          onClose={() => setCoverageGroup(null)}
        />
      )}

      {timeWindowEditor === null || config === null || selection === null ? null : (
        <TenantRoutingTimeWindowDialog
          configId={config.id}
          window={timeWindowEditor.window}
          targets={skuTargets.targetsBySku.get(selection.sku.skuId) ?? []}
          onClose={() => setTimeWindowEditor(null)}
        />
      )}

      <ConfirmDialog
        open={targetDeleteDraft !== null}
        onOpenChange={(next) => {
          if (!next && !deleteTarget.isPending) setTargetDeleteDraft(null);
        }}
        title={t('tenant_routing.pool.remove_confirm_title')}
        body={targetDeleteDraft === null ? undefined : (
          <span className="flex flex-col gap-2">
            <span className="font-mono text-paper">
              {targetDeleteDraft.sku.skuName} · {targetDeleteDraft.sku.skuId}
            </span>
            <span className="break-all font-mono text-paper-mid">
              {formatSkuTargetIdentity(targetDeleteDraft.target)} · {targetDeleteDraft.target.id}
            </span>
            {targetDeleteLastEnabledProtocols.length > 0 ? (
              <span
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-bad-line bg-bad-dim px-3 py-2.5 text-bad-bright"
                data-testid="tenant-routing-target-delete-last-enabled-danger"
              >
                <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex flex-col gap-1">
                  <strong>{t('tenant_routing.pool.remove_last_enabled_title', {
                    protocols: targetDeleteLastEnabledProtocols.join(', '),
                  })}</strong>
                  <span>{t('tenant_routing.pool.remove_last_enabled_body', {
                    sku: targetDeleteDraft.sku.skuName,
                    protocols: targetDeleteLastEnabledProtocols.join(', '),
                    count: String(config?.assigned_org_count ?? 0),
                  })}</span>
                </span>
              </span>
            ) : (
              <span className="text-paper-low">{t('tenant_routing.pool.remove_confirm_body')}</span>
            )}
          </span>
        )}
        confirmLabel={t('tenant_routing.pool.remove_target')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={handleTargetDeleteConfirm}
        tone="danger"
        loading={deleteTarget.isPending}
        testId="tenant-routing-target-delete-confirm"
      />

      <ConfirmDialog
        open={breakerResetDraft !== null}
        onOpenChange={(next) => {
          if (!next) setBreakerResetDraft(null);
        }}
        title={t('tenant_routing.breaker.reset_confirm_title')}
        body={breakerResetDraft === null ? undefined : (
          <span className="flex flex-col gap-2">
            <span className="break-all text-paper">{formatBreakerIdentity(breakerResetDraft)}</span>
            <span className="text-paper-low">{t('tenant_routing.breaker.reset_confirm_body')}</span>
          </span>
        )}
        confirmLabel={t('tenant_routing.breaker.reset')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={handleBreakerResetConfirm}
        loading={resetBreaker.isPending}
        testId="tenant-routing-breaker-reset-confirm"
      />

      <RoutingBreakerTripDialog
        target={breakerTripTarget}
        onClose={() => setBreakerTripTarget(null)}
      />
    </>
  );
}
